import { useState, type MouseEvent, type ReactNode } from 'react';
import Popover from '@mui/material/Popover';
import { overlayPaneProps } from './overlayPane';

/** Where the panel sits under its trigger: Angular `xPosition="before"` extends to the left, `"after"` to the right. */
export type MenuXPosition = 'before' | 'after';

export function useMenu() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  return {
    anchorEl,
    open: anchorEl !== null,
    /** Attach to the trigger's onClick. */
    onOpen: (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget),
    onClose: () => setAnchorEl(null),
  };
}

interface MenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Extra classes on the panel, e.g. "action-menu top-menu-list" (what Angular put in `class=`). */
  className?: string;
  xPosition?: MenuXPosition;
  children: ReactNode;
}

/**
 * Angular `<mat-menu>`: an overlay panel below its trigger. Like Angular's, ANY click inside the panel closes it;
 * content that must stay open (the notification list) stops propagation itself. The panel carries Angular's class
 * names so the ported global SCSS applies unchanged.
 */
export function Menu({ anchorEl, open, onClose, className = '', xPosition = 'after', children }: MenuProps) {
  const horizontal = xPosition === 'before' ? 'right' : 'left';

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal }}
      transformOrigin={{ vertical: 'top', horizontal }}
      slotProps={{
        root: {
          slotProps: {
            backdrop: {
              invisible: true,
              className: 'cdk-overlay-backdrop cdk-overlay-transparent-backdrop cdk-overlay-backdrop-showing',
            },
          },
        },
        paper: overlayPaneProps(),
      }}
    >
      {/* CDK overlay pane (the Popover paper) > mat-menu panel > content, exactly Material's nesting */}
      <div
        role="menu"
        tabIndex={-1}
        className={`mat-mdc-menu-panel mat-mdc-elevation-specific mat-menu-${xPosition} mat-menu-below ${className} mat-elevation-z8`.trim()}
        onClick={onClose}
      >
        <div className="mat-mdc-menu-content">{children}</div>
      </div>
    </Popover>
  );
}
